/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/* Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved. */

import React, { useState, useRef, useEffect } from 'react';

import { PageHeaderTitleRow } from './page-header/PageHeaderTitleRow';
import { PageHeaderSearchRow } from './page-header/PageHeaderSearchRow';
import { PageHeaderFilterChips } from './page-header/PageHeaderFilterChips';

interface PageHeaderProps {
  title: string;
  icon?: React.ReactNode;
  /** Omit both `ctaLabel` and `onCtaClick` to render the header without a CTA button. */
  ctaLabel?: string;
  searchPlaceholder: string;
  searchValue?: string;
  onCtaClick?: () => void;
  onSearchChange: (value: string) => void;
  onFiltersToggle?: () => void;
  activeFilters?: string[];
  onRemoveFilter?: (filter: string) => void;
  onClearAllFilters?: () => void;
  filterCount?: number;
  introLabel?: string;
  introBody?: string;
  introTestIdPrefix?: string;
  hideFilters?: boolean;
  /** Whether the list is narrowed to the caller's own favourites. */
  favoritesOnly?: boolean;
  /** Omitted hides the favourites star. */
  onToggleFavorites?: () => void;
  /** Optional action rendered to the right of the filters toggle. */
  trailingAction?: React.ReactNode;
  /** Optional action rendered to the LEFT of the primary CTA. */
  secondaryAction?: React.ReactNode;
  /** A visible one-liner next to the title. */
  subtitle?: string;
  /**
   * Drops the search row entirely, for an area that is a set of sections
   * rather than a searchable list -- Planilhas is the first one.
   */
  hideSearch?: boolean;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  icon,
  ctaLabel,
  searchPlaceholder,
  searchValue: controlledValue,
  onCtaClick,
  onSearchChange,
  onFiltersToggle,
  activeFilters,
  onRemoveFilter,
  onClearAllFilters,
  filterCount,
  introLabel,
  introBody,
  introTestIdPrefix,
  hideFilters,
  favoritesOnly,
  onToggleFavorites,
  trailingAction,
  secondaryAction,
  subtitle,
  hideSearch,
}) => {
  const [internalValue, setInternalValue] = useState(controlledValue ?? '');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (controlledValue !== undefined && controlledValue !== internalValue) {
      setInternalValue(controlledValue);
    }
  }, [controlledValue]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInternalValue(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onSearchChange(val);
    }, 300);
  };

  return (
    <div
      data-tour="page-header"
      style={{
        padding: 'var(--space-4) var(--space-5)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border-subtle)',
        background: 'var(--surface-raised)',
        boxShadow: 'var(--shadow-md)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
        fontFamily: 'var(--font-family)',
        overflow: 'visible',
      }}
    >
      <PageHeaderTitleRow
        title={title}
        icon={icon}
        ctaLabel={ctaLabel}
        onCtaClick={onCtaClick}
        introText={[introLabel, introBody].filter(Boolean).join('. ')}
        introTestIdPrefix={introTestIdPrefix}
        subtitle={subtitle}
        secondaryAction={secondaryAction}
      />
      {!hideSearch && (
      <PageHeaderSearchRow
        searchPlaceholder={searchPlaceholder}
        favoritesOnly={favoritesOnly}
        onToggleFavorites={onToggleFavorites}
        searchValue={internalValue}
        onSearchInputChange={handleSearchChange}
        onFiltersToggle={onFiltersToggle ?? (() => {})}
        filterCount={filterCount ?? 0}
        hideFilters={hideFilters}
        trailingAction={trailingAction}
      />
      )}
      {activeFilters && onRemoveFilter && onClearAllFilters && (
        <PageHeaderFilterChips
          activeFilters={activeFilters}
          onRemoveFilter={onRemoveFilter}
          onClearAllFilters={onClearAllFilters}
        />
      )}
    </div>
  );
};
